import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Headphones,
  LoaderCircle,
  LockKeyhole,
  MessageCircle,
  Send,
  UserRound,
  UsersRound,
  Wifi,
  X,
} from 'lucide-react';
import { supportChatService } from '../../services/supportChat.service';

const POLL_INTERVAL_MS = 4000;
const PRESENCE_POLL_INTERVAL_MS = 5000;
const RECENT_CONVERSATION_KEY = 'hostul_support_chat_recent';
const RECENT_CONVERSATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const SEEN_SUPPORT_MESSAGES_KEY = 'hostul:public-chat:seen-support:v2';

const AGENT_STATUS_LABEL = {
  online: 'Online agora',
  busy: 'Atendendo agora',
  away: 'Disponivel em breve',
};

const messageTime = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const closedAtLabel = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const messageIdentity = (message) => {
  if (message?.sender === 'ai') {
    return 'Agente Virtual Hostul';
  }
  if (message?.sender === 'agent') {
    return message?.agentName || 'Suporte Hostul';
  }
  return '';
};

const getInitials = (name) => {
  const initials = String(name || '')
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return initials || 'SH';
};

const normalizeHistory = (history) => ({
  session: history?.session || null,
  messages: Array.isArray(history?.messages) ? history.messages : [],
});

const appendUniqueMessages = (currentMessages, incomingMessages) => {
  const knownIds = new Set(
    currentMessages.map((currentMessage) => currentMessage?.id).filter(Boolean),
  );
  const uniqueIncoming = incomingMessages.filter((incomingMessage) => {
    if (!incomingMessage?.id || !knownIds.has(incomingMessage.id)) {
      if (incomingMessage?.id) {
        knownIds.add(incomingMessage.id);
      }
      return true;
    }
    return false;
  });
  return [...currentMessages, ...uniqueIncoming];
};

const rememberRecentConversation = () => {
  try {
    window.localStorage.setItem(RECENT_CONVERSATION_KEY, String(Date.now()));
  } catch {
    // A sessão no servidor continua sendo a fonte principal quando o storage está indisponível.
  }
};

const forgetRecentConversation = () => {
  try {
    window.localStorage.removeItem(RECENT_CONVERSATION_KEY);
  } catch {
    // Sem ação necessária quando o storage está indisponível.
  }
};

const hasRecentConversationMarker = () => {
  try {
    const storedAt = Number(window.localStorage.getItem(RECENT_CONVERSATION_KEY));
    return Number.isFinite(storedAt)
      && storedAt > 0
      && Date.now() - storedAt <= RECENT_CONVERSATION_TTL_MS;
  } catch {
    return false;
  }
};

const supportMessageTimestamp = (message) => {
  if (message?.sender !== 'agent') {
    return Number.NaN;
  }
  const timestamp = new Date(message.timestamp).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.NaN;
};

const isUnreadSupportMessage = (message) => (
  message?.sender === 'agent'
  && message.isRead !== true
  && !message.readAt
);

const unreadSupportMessageIds = (messages) => (
  messages
    .filter(isUnreadSupportMessage)
    .map((message) => message.id)
    .filter(Boolean)
);

const latestSupportMessageTimestamp = (messages) => (
  messages.reduce((latest, message) => {
    const timestamp = supportMessageTimestamp(message);
    return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest;
  }, 0)
);

const readSeenSupportMessages = () => {
  try {
    const rawValue = window.localStorage.getItem(SEEN_SUPPORT_MESSAGES_KEY);
    if (!rawValue) {
      return {};
    }
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeSeenSupportMessages = (seenMessages) => {
  try {
    window.localStorage.setItem(SEEN_SUPPORT_MESSAGES_KEY, JSON.stringify(seenMessages));
  } catch {
    // A contagem segue funcionando em memória quando o storage está indisponível.
  }
};

const markSupportMessagesSeen = (sessionId, messages) => {
  if (!sessionId) {
    return;
  }
  const seenMessages = readSeenSupportMessages();
  const latestSupportAt = latestSupportMessageTimestamp(messages);
  const seenAt = latestSupportAt > 0 ? latestSupportAt : Date.now();
  seenMessages[sessionId] = Math.max(Number(seenMessages[sessionId]) || 0, seenAt);
  writeSeenSupportMessages(seenMessages);
};

const countUnreadSupportMessages = (sessionId, messages) => {
  if (!sessionId) {
    return 0;
  }
  const seenMessages = readSeenSupportMessages();
  const seenAt = Number(seenMessages[sessionId]);

  if (!Number.isFinite(seenAt)) {
    const unreadByReceipt = messages.filter(isUnreadSupportMessage).length;
    if (unreadByReceipt > 0) {
      return unreadByReceipt;
    }

    const latestSupportAt = latestSupportMessageTimestamp(messages);
    seenMessages[sessionId] = latestSupportAt > 0 ? latestSupportAt : Date.now();
    writeSeenSupportMessages(seenMessages);
    return 0;
  }

  return messages.filter((message) => {
    const timestamp = supportMessageTimestamp(message);
    return isUnreadSupportMessage(message)
      && Number.isFinite(timestamp)
      && timestamp > seenAt;
  }).length;
};

const isDocumentVisible = () => (
  typeof document === 'undefined' || document.visibilityState === 'visible'
);

const AgentAvatar = ({ agent, compact = false }) => {
  const sizeClass = compact ? 'h-7 w-7 text-[10px]' : 'h-10 w-10 text-xs';
  const ringPaddingClass = compact ? 'p-[2px]' : 'p-[3px]';
  const accentColor = agent?.accentColor && /^#[0-9a-fA-F]{6}$/.test(agent.accentColor)
    ? agent.accentColor
    : '#7c3aed';

  if (!agent) {
    return (
      <div className={`${sizeClass} flex items-center justify-center rounded-full bg-white bg-opacity-20`}>
        <Headphones size={compact ? 14 : 18} className="text-white" />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} ${ringPaddingClass} flex items-center justify-center rounded-full font-semibold text-white shadow-sm`}
      style={{
        backgroundColor: accentColor,
        boxShadow: '0 8px 18px rgba(88, 28, 135, 0.2)',
      }}
    >
      <div
        className="flex h-full w-full items-center justify-center overflow-hidden rounded-full"
        style={{
          background: agent.avatar
            ? '#ffffff'
            : `linear-gradient(135deg, ${accentColor}, rgba(88, 28, 135, 0.88))`,
        }}
      >
        {agent.avatar ? (
          <img src={agent.avatar} alt="" className="h-full w-full object-cover" />
        ) : (
          getInitials(agent.displayName)
        )}
      </div>
    </div>
  );
};

const ChatHeader = ({
  assignedAgent,
  onlineAgents,
  onClose,
  session,
}) => {
  const isClosed = session?.status === 'closed';
  const hasOnlineAgents = onlineAgents.length > 0;
  const firstAgent = onlineAgents[0];
  const hasAssignedAgent = Boolean(assignedAgent);
  const assignedAgentOnline = Boolean(assignedAgent?.online);
  const headerTitle = assignedAgent?.displayName || 'Suporte Hostul';
  const presenceLabel = assignedAgent
    ? assignedAgentOnline
      ? AGENT_STATUS_LABEL[assignedAgent.status || 'online'] || AGENT_STATUS_LABEL.online
      : 'Indisponivel no momento'
    : !hasOnlineAgents
      ? 'Suporte recebendo mensagens'
      : onlineAgents.length === 1
        ? `${firstAgent.displayName} online`
        : `${onlineAgents.length} atendentes online`;
  const detailLabel = assignedAgent
    ? assignedAgent.title || 'Suporte Hostul'
    : hasOnlineAgents && onlineAgents.length === 1
      ? `${firstAgent.title || 'Suporte Hostul'} · ${
        AGENT_STATUS_LABEL[firstAgent.status || 'online'] || AGENT_STATUS_LABEL.online
      }`
      : hasOnlineAgents
        ? 'Equipe Hostul disponivel'
        : 'Responderemos assim que possivel';
  const avatarAgent = assignedAgent || firstAgent;
  const showAgentStack = !hasAssignedAgent && onlineAgents.length > 1;
  const isVisiblyOnline = hasAssignedAgent ? assignedAgentOnline : hasOnlineAgents;

  if (isClosed) {
    const agentName = session?.closedByAgentName
      || session?.agentName
      || assignedAgent?.displayName
      || 'Suporte Hostul';
    const agentTitle = assignedAgent?.title || 'Suporte Hostul';
    const formattedClosedAt = closedAtLabel(session?.closedAt);
    const closedAgent = {
      displayName: agentName,
      title: agentTitle,
      avatar: assignedAgent?.avatar,
      accentColor: assignedAgent?.accentColor,
    };

    return (
      <header className="bg-gradient-to-br from-purple-700 via-purple-700 to-purple-800 px-5 py-4 text-white shadow-lg">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <CheckCircle2 size={16} className="shrink-0 text-emerald-200" />
            <h2 id="support-chat-title" className="min-w-0 text-sm font-semibold leading-snug">
              Atendimento finalizado
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 transition-all duration-200 hover:scale-105 hover:bg-purple-900"
            aria-label="Fechar chat"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-3 flex items-start gap-3 rounded-xl border border-white/15 bg-white/10 px-3 py-3 shadow-sm">
          <AgentAvatar agent={closedAgent} />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium text-purple-100">Atendido por</p>
            <p className="break-words text-sm font-semibold leading-snug text-white">{agentName}</p>
            <div className="mt-1 space-y-0.5 text-xs leading-snug text-purple-100">
              <p className="break-words">{agentTitle}</p>
              {formattedClosedAt && <p>Finalizado em {formattedClosedAt}</p>}
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="flex items-center justify-between bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 text-white shadow-lg">
      <div className="flex min-w-0 items-center space-x-3">
        <div className="relative flex shrink-0 items-center">
          {showAgentStack ? (
            <div className="flex -space-x-2">
              {onlineAgents.slice(0, 3).map((agent) => (
                <AgentAvatar key={agent.id} agent={agent} compact />
              ))}
            </div>
          ) : (
            <AgentAvatar agent={avatarAgent} />
          )}
          <div className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-white ${
            isVisiblyOnline ? 'bg-green-400' : 'bg-amber-300'
          }`}
          />
        </div>

        <div className="min-w-0">
          <h2 id="support-chat-title" className="truncate text-sm font-semibold">{headerTitle}</h2>
          <div className="flex min-w-0 items-center space-x-1">
            {!hasAssignedAgent && hasOnlineAgents ? (
              <UsersRound size={12} className="shrink-0 text-green-300" />
            ) : (
              <Wifi
                size={12}
                className={`shrink-0 ${isVisiblyOnline ? 'text-green-300' : 'text-purple-200'}`}
              />
            )}
            <span className="truncate text-xs font-medium text-purple-50">{presenceLabel}</span>
          </div>
          <p className="truncate text-[11px] text-purple-100">{detailLabel}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="shrink-0 rounded-full p-2 transition-all duration-200 hover:scale-105 hover:bg-purple-800"
        aria-label="Fechar chat"
      >
        <X size={18} />
      </button>
    </header>
  );
};

const SupportChatLauncher = ({ onOpen, unreadCount = 0 }) => {
  const unreadLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className="relative group flex h-14 w-14 items-center justify-center sm:h-16 sm:w-16">
      <button
        type="button"
        onClick={onOpen}
        aria-label="Abrir chat de suporte"
        className="blob-button relative z-10 transform rounded-full border border-purple-400/20 p-3 text-white shadow-2xl backdrop-blur-sm transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-purple-400 focus:ring-opacity-50 hover:scale-110 group-hover:shadow-purple-500/25"
      >
        <div className="blob-button-bg absolute inset-0" />
        <div className="static-icon-container relative z-10">
          <MessageCircle size={20} className="static-icon drop-shadow-lg" />
        </div>
      </button>

      {unreadCount > 0 && (
        <span
          className="absolute -right-1 -top-1 z-20 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-none text-white shadow-lg"
          aria-label={`${unreadCount} mensagens nao lidas do suporte`}
        >
          {unreadLabel}
        </span>
      )}
    </div>
  );
};

const PublicSupportChat = ({ open, onOpen, onClose }) => {
  const [phase, setPhase] = useState('loading');
  const [mode, setMode] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [onlineAgents, setOnlineAgents] = useState([]);
  const [assignedAgentProfile, setAssignedAgentProfile] = useState(null);
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  const messagesEndRef = useRef(null);
  const emailRef = useRef(null);

  const assignedAgent = useMemo(() => {
    const agentId = session?.agentId || session?.closedByAgentId;
    if (!agentId) {
      return undefined;
    }
    if (assignedAgentProfile?.id === agentId) {
      return assignedAgentProfile;
    }
    const onlineProfile = onlineAgents.find((agent) => agent.id === agentId);
    if (onlineProfile) {
      return { ...onlineProfile, online: true };
    }
    return {
      id: agentId,
      displayName: session?.closedByAgentName || session?.agentName || 'Suporte Hostul',
      title: 'Suporte Hostul',
      accentColor: '#7c3aed',
      status: 'away',
      online: false,
    };
  }, [
    assignedAgentProfile,
    onlineAgents,
    session?.agentId,
    session?.agentName,
    session?.closedByAgentId,
    session?.closedByAgentName,
  ]);

  useEffect(() => {
    if (open) {
      return undefined;
    }

    let cancelled = false;
    const hasLocalMarker = hasRecentConversationMarker();
    if (hasLocalMarker) {
      setHasStartedConversation(true);
    }

    const restoreRecentConversation = async () => {
      try {
        const authenticated = await supportChatService.findAuthenticatedSession();
        if (cancelled) {
          return;
        }

        if (authenticated.authenticated) {
          const activeSession = authenticated.session?.status !== 'closed'
            ? authenticated.session
            : null;
          if (activeSession?.id) {
            setMode('authenticated');
            setSession(activeSession);
            setHasStartedConversation(true);
            rememberRecentConversation();
          } else {
            setSession(null);
            setMessages([]);
            setHasStartedConversation(false);
            setUnreadSupportCount(0);
            forgetRecentConversation();
          }
          return;
        }

        const guestHistory = normalizeHistory(await supportChatService.findGuestSession());
        if (cancelled) {
          return;
        }

        if (guestHistory.session?.id && guestHistory.session.status !== 'closed') {
          setMode('guest');
          setSession(guestHistory.session);
          setMessages(guestHistory.messages);
          setHasStartedConversation(true);
          setUnreadSupportCount(
            countUnreadSupportMessages(guestHistory.session.id, guestHistory.messages),
          );
          rememberRecentConversation();
        } else {
          setSession(null);
          setMessages([]);
          setHasStartedConversation(false);
          setUnreadSupportCount(0);
          forgetRecentConversation();
        }
      } catch {
        if (!cancelled && !hasLocalMarker) {
          setHasStartedConversation(false);
          setUnreadSupportCount(0);
        }
      }
    };

    void restoreRecentConversation();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    const loadPresence = async () => {
      try {
        const agents = await supportChatService.getOnlineAgents();
        if (!cancelled) {
          setOnlineAgents(Array.isArray(agents) ? agents : []);
        }
      } catch {
        // O cabeçalho mantém o estado de recebimento mesmo se a presença ficar indisponível.
      }
    };
    const refreshOnFocus = () => {
      void loadPresence();
    };
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        void loadPresence();
      }
    };

    void loadPresence();
    const interval = window.setInterval(loadPresence, PRESENCE_POLL_INTERVAL_MS);
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
    };
  }, [open]);

  useEffect(() => {
    const sessionId = session?.id;
    if (open || !hasStartedConversation || !sessionId || !mode) {
      return undefined;
    }

    let cancelled = false;
    const syncUnreadMessages = async () => {
      try {
        const history = mode === 'authenticated'
          ? await supportChatService.getAuthenticatedHistory(sessionId)
          : await supportChatService.getGuestHistory(sessionId);
        if (cancelled || (!history?.session && !Array.isArray(history?.messages))) {
          return;
        }

        const normalized = normalizeHistory(history);
        setSession((current) => normalized.session || current);
        setMessages(normalized.messages);
        setUnreadSupportCount(
          countUnreadSupportMessages(sessionId, normalized.messages),
        );
      } catch {
        // Uma falha transitória não remove o contador já exibido.
      }
    };
    const syncOnFocus = () => {
      void syncUnreadMessages();
    };
    const syncOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        void syncUnreadMessages();
      }
    };

    void syncUnreadMessages();
    const interval = window.setInterval(syncUnreadMessages, PRESENCE_POLL_INTERVAL_MS);
    window.addEventListener('focus', syncOnFocus);
    document.addEventListener('visibilitychange', syncOnVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', syncOnFocus);
      document.removeEventListener('visibilitychange', syncOnVisibility);
    };
  }, [hasStartedConversation, mode, open, session?.id]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    const initialize = async () => {
      setPhase('loading');
      setError('');
      try {
        const authenticated = await supportChatService.findAuthenticatedSession();
        if (cancelled) {
          return;
        }

        if (authenticated.authenticated) {
          const selectedSession = authenticated.session
            || await supportChatService.createAuthenticatedSession();
          const history = normalizeHistory(
            await supportChatService.getAuthenticatedHistory(selectedSession.id),
          );
          if (!cancelled) {
            const resolvedSession = history.session || selectedSession;
            if (!resolvedSession?.id) {
              throw new Error('Sessão de atendimento inválida');
            }
            setMode('authenticated');
            setSession(resolvedSession);
            setMessages(history.messages);
            setHasStartedConversation(true);
            if (isDocumentVisible()) {
              markSupportMessagesSeen(resolvedSession.id, history.messages);
              setUnreadSupportCount(0);
            } else {
              setUnreadSupportCount(
                countUnreadSupportMessages(resolvedSession.id, history.messages),
              );
            }
            rememberRecentConversation();
            setPhase('chat');
          }
          return;
        }

        const guestHistory = normalizeHistory(await supportChatService.findGuestSession());
        if (!cancelled && guestHistory.session && guestHistory.session.status !== 'closed') {
          setMode('guest');
          setSession(guestHistory.session);
          setMessages(guestHistory.messages);
          setHasStartedConversation(true);
          if (isDocumentVisible()) {
            markSupportMessagesSeen(guestHistory.session.id, guestHistory.messages);
            setUnreadSupportCount(0);
          } else {
            setUnreadSupportCount(
              countUnreadSupportMessages(guestHistory.session.id, guestHistory.messages),
            );
          }
          rememberRecentConversation();
          setPhase('chat');
          return;
        }

        if (!cancelled) {
          setMode('guest');
          setSession(null);
          setMessages([]);
          setUnreadSupportCount(0);
          setPhase('contact');
        }
      } catch {
        if (!cancelled) {
          setError('Não foi possível conectar ao atendimento agora. Tente novamente.');
          setPhase('error');
        }
      }
    };

    void initialize();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    const agentId = session?.agentId || session?.closedByAgentId;
    setAssignedAgentProfile(null);
    if (!open || !agentId) {
      return undefined;
    }

    let cancelled = false;
    supportChatService.getSupportAgentProfile(agentId)
      .then((profile) => {
        if (!cancelled && profile?.id === agentId) {
          setAssignedAgentProfile(profile);
        }
      })
      .catch(() => {
        // Nome e estado da sessão continuam sendo usados como fallback.
      });

    return () => {
      cancelled = true;
    };
  }, [open, session?.agentId, session?.closedByAgentId]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open && phase === 'contact') {
      emailRef.current?.focus();
    }
  }, [open, phase]);

  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const sessionId = session?.id;
    if (!open || phase !== 'chat' || !sessionId) {
      return undefined;
    }

    const markCurrentMessagesSeen = () => {
      if (!isDocumentVisible()) {
        return;
      }
      markSupportMessagesSeen(sessionId, messages);
      setUnreadSupportCount(0);
      if (mode === 'authenticated') {
        const messageIds = unreadSupportMessageIds(messages);
        if (messageIds.length > 0) {
          void supportChatService.markAuthenticatedMessagesRead(sessionId, messageIds)
            .catch(() => {
              // O marcador local evita repetir o alerta se o recibo falhar temporariamente.
            });
        }
      }
    };

    markCurrentMessagesSeen();
    document.addEventListener('visibilitychange', markCurrentMessagesSeen);
    return () => {
      document.removeEventListener('visibilitychange', markCurrentMessagesSeen);
    };
  }, [messages, mode, open, phase, session?.id]);

  useEffect(() => {
    const sessionId = session?.id;
    if (!open || phase !== 'chat' || !sessionId || !mode) {
      return undefined;
    }

    let cancelled = false;
    const refresh = async () => {
      try {
        const history = mode === 'authenticated'
          ? await supportChatService.getAuthenticatedHistory(sessionId)
          : await supportChatService.getGuestHistory(sessionId);
        if (!cancelled && (history?.session || Array.isArray(history?.messages))) {
          const normalized = normalizeHistory(history);
          setSession((current) => normalized.session || current);
          setMessages(normalized.messages);
          if (isDocumentVisible()) {
            markSupportMessagesSeen(sessionId, normalized.messages);
            setUnreadSupportCount(0);
          } else {
            setUnreadSupportCount(
              countUnreadSupportMessages(sessionId, normalized.messages),
            );
          }
        }
      } catch {
        // Uma falha transitória de polling não interrompe o texto que está sendo digitado.
      }
    };

    const interval = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [mode, open, phase, session?.id]);

  const handleStartGuestChat = async (event) => {
    event.preventDefault();
    setIsStarting(true);
    setError('');
    try {
      const history = normalizeHistory(
        await supportChatService.createGuestSession(contact.email, contact.phone),
      );
      if (!history.session?.id) {
        throw new Error('Não foi possível iniciar a conversa.');
      }
      setMode('guest');
      setSession(history.session);
      setMessages(history.messages);
      setHasStartedConversation(true);
      markSupportMessagesSeen(history.session.id, history.messages);
      setUnreadSupportCount(0);
      rememberRecentConversation();
      setPhase('chat');
    } catch (requestError) {
      setError(requestError?.message || 'Confira os dados informados e tente novamente.');
    } finally {
      setIsStarting(false);
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const content = message.trim();
    if (!content || !session?.id || isSending) {
      return;
    }

    const optimisticMessageId = `optimistic-${session.id}-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticMessageId,
      sessionId: session.id,
      sender: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    setIsSending(true);
    setError('');
    setMessage('');
    setMessages((current) => appendUniqueMessages(current, [optimisticMessage]));
    try {
      const result = mode === 'authenticated'
        ? await supportChatService.sendAuthenticatedMessage(session.id, content)
        : await supportChatService.sendGuestMessage(session.id, content);
      const newMessages = [result?.userMessage, result?.aiMessage].filter(Boolean);
      setMessages((current) => appendUniqueMessages(
        current.filter((currentMessage) => currentMessage.id !== optimisticMessageId),
        newMessages,
      ));
    } catch (requestError) {
      setMessages((current) => (
        current.filter((currentMessage) => currentMessage.id !== optimisticMessageId)
      ));
      setMessage(content);
      setError(requestError?.message || 'Não foi possível enviar sua mensagem.');
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenChat = () => {
    if (session?.id) {
      markSupportMessagesSeen(session.id, messages);
    }
    setUnreadSupportCount(0);
    onOpen();
  };

  if (!open && !hasStartedConversation) {
    return null;
  }

  if (!open) {
    return (
      <div className="fixed bottom-4 right-4 z-[100] sm:bottom-6 sm:right-6">
        <SupportChatLauncher
          onOpen={handleOpenChat}
          unreadCount={unreadSupportCount}
        />
      </div>
    );
  }

  const canSend = session?.status !== 'closed' && Boolean(message.trim()) && !isSending;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
      <section
        role="dialog"
        aria-labelledby="support-chat-title"
        className="flex h-[460px] max-h-[70vh] w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl sm:h-[500px] sm:w-96"
      >
        <ChatHeader
          assignedAgent={assignedAgent}
          onlineAgents={onlineAgents}
          onClose={onClose}
          session={session}
        />

        {phase === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-white px-6 text-center">
            <LoaderCircle className="animate-spin text-purple-600" size={32} />
            <p className="text-sm text-gray-600">Conectando ao atendimento...</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-white px-8 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <AlertCircle className="text-red-500" size={30} />
            </span>
            <p className="text-sm leading-relaxed text-gray-600">{error}</p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-purple-600 px-5 py-2.5 font-medium text-white transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Fechar
            </button>
          </div>
        )}

        {phase === 'contact' && (
          <div className="flex flex-1 flex-col justify-center overflow-y-auto bg-white px-6 py-6">
            <div className="mb-5">
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-purple-200 text-purple-600">
                <UserRound size={24} />
              </span>
              <h3 className="mb-1.5 text-lg font-semibold text-gray-800">Antes de começar</h3>
              <p className="text-sm leading-relaxed text-gray-500">
                Informe seu email e um telefone para contato. Usaremos esses dados apenas para identificar e acompanhar este atendimento.
              </p>
            </div>

            <form onSubmit={handleStartGuestChat} className="space-y-3.5">
              <div>
                <label htmlFor="support-chat-email" className="mb-1.5 block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  ref={emailRef}
                  id="support-chat-email"
                  type="email"
                  autoComplete="email"
                  required
                  maxLength={254}
                  value={contact.email}
                  onChange={(event) => setContact((current) => ({ ...current, email: event.target.value }))}
                  placeholder="voce@email.com"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label htmlFor="support-chat-phone" className="mb-1.5 block text-sm font-medium text-gray-700">
                  Número para contato
                </label>
                <input
                  id="support-chat-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  maxLength={32}
                  value={contact.phone}
                  onChange={(event) => setContact((current) => ({ ...current, phone: event.target.value }))}
                  placeholder="(27) 99999-9999"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {error && (
                <p role="alert" className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
                  <AlertCircle size={17} className="mt-0.5 shrink-0" />
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isStarting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-3 font-semibold text-white shadow-lg transition hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isStarting ? <LoaderCircle className="animate-spin" size={18} /> : <MessageCircle size={18} />}
                {isStarting ? 'Iniciando...' : 'Iniciar conversa'}
              </button>

              <p className="flex items-center justify-center gap-1.5 text-[11px] text-gray-500">
                <LockKeyhole size={13} />
                Seus dados e o acesso à conversa são protegidos.
              </p>
            </form>
          </div>
        )}

        {phase === 'chat' && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div
              className="chat-scrollbar-hidden flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 to-white p-6"
              aria-live="polite"
            >
              <div className="space-y-4">
                {messages.length === 0 && (
                  <div className="py-8 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-purple-200">
                      <MessageCircle size={24} className="text-purple-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-600">Conversa iniciada!</p>
                    <p className="mt-1 text-xs text-gray-500">Nossa equipe receberá sua mensagem</p>
                  </div>
                )}

                {messages.map((chatMessage) => {
                  const fromUser = chatMessage.sender === 'user';
                  const fromSystem = chatMessage.sender === 'system';
                  const identity = messageIdentity(chatMessage);
                  return (
                    <div
                      key={chatMessage.id}
                      className={`flex items-start space-x-3 ${
                        fromUser ? 'justify-end' : fromSystem ? 'justify-center' : 'justify-start'
                      }`}
                    >
                      {!fromUser && !fromSystem && (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-purple-200">
                          <Headphones size={16} className="text-purple-600" />
                        </div>
                      )}

                      <div className={`max-w-xs rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        fromUser
                          ? 'ml-auto bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg'
                          : fromSystem
                            ? 'mx-auto w-full max-w-md border border-blue-200 bg-blue-50 text-center text-blue-800'
                            : 'mr-auto border border-gray-100 bg-white text-gray-800 shadow-md'
                      }`}
                      >
                        {identity && (
                          <p className="mb-1 text-[11px] font-semibold text-gray-500">{identity}</p>
                        )}
                        {chatMessage.content && (
                          <p className="whitespace-pre-wrap break-words">{chatMessage.content}</p>
                        )}
                        <p className={`mt-2 text-xs ${
                          fromUser
                            ? 'text-purple-200'
                            : fromSystem
                              ? 'text-blue-600'
                              : 'text-gray-400'
                        }`}
                        >
                          {messageTime(chatMessage.timestamp)}
                        </p>
                      </div>

                      {fromUser && (
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-purple-700">
                          <UserRound size={16} className="text-purple-200" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-gray-100 bg-white p-6 shadow-lg">
              {error && (
                <p role="alert" className="mb-3 flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle size={16} />
                  {error}
                </p>
              )}
              <form onSubmit={handleSend} className="flex items-end space-x-3">
                <label htmlFor="support-chat-message" className="sr-only">Digite sua mensagem</label>
                <textarea
                  id="support-chat-message"
                  rows={1}
                  maxLength={12000}
                  value={message}
                  disabled={session?.status === 'closed' || isSending}
                  onChange={(event) => setMessage(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      event.currentTarget.form?.requestSubmit();
                    }
                  }}
                  placeholder={session?.status === 'closed' ? 'Conversa finalizada' : 'Digite sua mensagem...'}
                  className="min-h-[48px] max-h-[120px] flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-purple-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
                <button
                  type="submit"
                  disabled={!canSend}
                  aria-label="Enviar mensagem"
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    canSend
                      ? 'transform bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg hover:scale-105 hover:from-purple-700 hover:to-purple-800 hover:shadow-xl'
                      : 'cursor-not-allowed bg-gray-100 text-gray-400'
                  }`}
                >
                  {isSending ? <LoaderCircle className="animate-spin" size={20} /> : <Send size={20} />}
                </button>
              </form>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default PublicSupportChat;
